import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useCurrentCompany } from 'src/modules/companies/hooks';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import type { ActivationGateDialogProps, GateReason } from '../components/DynamicCampaignBuilder/ActivationGateDialog';

interface UseActivationGateParams {
  /**
   * Saves the campaign as a draft — the escape hatch offered by both variants.
   * `preventNavigation` is set only when the dialog itself redirects afterwards.
   */
  saveAsDraft: (options?: { preventNavigation?: boolean }) => Promise<unknown> | unknown;
}

/**
 * Blocks activation until the company has finished onboarding and holds an active
 * subscription.
 *
 * Sandbox companies bypass the gate entirely, and it only applies when activating
 * — saving a draft is always allowed, which is exactly what the dialog offers.
 * The hook decides; ActivationGateDialog draws.
 */
export function useActivationGate({ saveAsDraft }: UseActivationGateParams) {
  const navigate = useNavigate();
  const companyId = useSelector(getCurrentCompanyId);
  const { company } = useCurrentCompany();
  const [reason, setReason] = useState<GateReason | null>(null);

  /** True when activation is blocked; the dialog explains which requirement failed. */
  const blocksActivation = useCallback(() => {
    if (!company || company.sandBoxMode) return false;

    if (company.onboardingStatus !== 'completed') {
      setReason('onboarding');
      return true;
    }

    if (company.subscription?.status !== 'active') {
      setReason('subscription');
      return true;
    }

    return false;
  }, [company]);

  /** Force the account-setup variant open — used when the API rejects the save. */
  const showOnboardingGate = useCallback(() => setReason('onboarding'), []);

  const activationGate = useMemo<ActivationGateDialogProps>(() => {
    const close = () => setReason(null);
    return {
      reason,
      onClose: close,
      onSaveAsDraft: async () => {
        close();
        await saveAsDraft();
      },
      onCompleteSetup: async () => {
        close();
        await saveAsDraft({ preventNavigation: true });
        navigate(`/onboarding${companyId ? `?companyId=${companyId}` : ''}`);
      },
    };
  }, [reason, saveAsDraft, navigate, companyId]);

  return { blocksActivation, showOnboardingGate, activationGate };
}
